import React, { useContext, useEffect, useState } from "react";
import { Box, ClickAwayListener, Paper, Popper, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";
import api from "../../services/api";

const seenInMemory = new Map();
const useStyles = makeStyles(theme => ({
  popper: { zIndex: theme.zIndex.modal + 1 },
  paper: {
    width: 360,
    maxWidth: "calc(100vw - 32px)",
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[2],
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    overflow: "hidden"
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  icon: { color: theme.palette.primary.main, fontSize: 22 },
  title: { flex: 1, fontWeight: 600, fontSize: "0.95rem" },
  body: { padding: theme.spacing(2) },
  message: { fontSize: "0.875rem", lineHeight: 1.6, color: theme.palette.text.secondary }
}));

export default function MaintenanceNotice({ anchorRef }) {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const socketManager = useContext(SocketContext);
  const [notice, setNotice] = useState(null);
  const companyId = user?.companyId;
  const userId = user?.id;

  useEffect(() => {
    if (!companyId || !userId) return undefined;
    let active = true;
    let inFlight = false;
    let closeTimer;
    let retryTimer;
    const key = `neozapp:maintenance-seen:${companyId}:${userId}`;
    setNotice(null);

    const check = async () => {
      if (!active || inFlight || document.visibilityState === "hidden") return;
      inFlight = true;
      const requestedAt = Date.now();
      try {
        const { data } = await api.get("/auth/maintenance-notice", { timeout: 5000 });
        const next = data?.notice;
        if (!active || !next?.id || !(next.remainingMs > Date.now() - requestedAt)) return;
        if (seenInMemory.get(key) === next.id) return;
        try {
          if (localStorage.getItem(key) === next.id) return;
        } catch (_) { /* storage may be unavailable */ }
        seenInMemory.set(key, next.id);
        try { localStorage.setItem(key, next.id); } catch (_) { /* memory still deduplicates this session */ }
        setNotice(next);
        clearTimeout(closeTimer);
        closeTimer = setTimeout(() => { if (active) setNotice(null); }, 5000);
      } catch (_) {
        // Informational only: an unavailable notice must never disrupt the session.
      } finally {
        inFlight = false;
      }
    };
    const checkAfterRecovery = () => {
      check();
      clearTimeout(retryTimer);
      // Completion may be recorded shortly after the socket reconnects.
      retryTimer = setTimeout(check, 15000);
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") checkAfterRecovery();
    };
    checkAfterRecovery();
    const socket = socketManager?.getSocket(companyId);
    if (socket) socket.on("ready", checkAfterRecovery);
    window.addEventListener("neozapp:auth-restored", checkAfterRecovery);
    window.addEventListener("online", checkAfterRecovery);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      active = false;
      clearTimeout(closeTimer);
      clearTimeout(retryTimer);
      if (socket) socket.off("ready", checkAfterRecovery);
      window.removeEventListener("neozapp:auth-restored", checkAfterRecovery);
      window.removeEventListener("online", checkAfterRecovery);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [companyId, userId, socketManager]);

  useEffect(() => {
    if (!notice) return undefined;
    const closeOnEscape = event => { if (event.key === "Escape") setNotice(null); };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [notice]);

  if (!notice || !anchorRef.current) return null;
  return (
    <Popper open anchorEl={anchorRef.current} placement="bottom-end" className={classes.popper}
      modifiers={{ offset: { offset: "0, 10" }, preventOverflow: { boundariesElement: "viewport", padding: 16 } }}>
      <ClickAwayListener onClickAway={() => setNotice(null)}>
        <Paper className={classes.paper} elevation={2}>
          <Box className={classes.header}>
            <CheckCircleOutlineIcon className={classes.icon} />
            <Typography component="h2" className={classes.title}>{notice.title}</Typography>
          </Box>
          <Box className={classes.body} role="status" aria-live="polite" aria-atomic="true">
            <Typography className={classes.message}>{notice.message}</Typography>
          </Box>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
}
